// src/pages/chat/ChatThreadPage.tsx
// Individual conversation view with V2 streaming support

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Menu as MenuIcon,
  MoreVertical,
  CheckCircle,
  Archive,
  Trash2,
  Edit3,
  AlertCircle,
  Share2,
} from 'lucide-react';
import { clsx } from 'clsx';
import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';

import { chatApi } from '@/services/chat-api';
import { handleApiError } from '@/services/api';
import Button from '@/components/ui/Button';
import { ConfirmationModal } from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { MessageList, MessageInput, ArtifactPreviewPanel } from '@/components/chat';
import ToolCallDrawer from '@/components/chat/ToolCallDrawer';
import { useChatLayout } from '@/components/layout/ChatLayout';
import { useStreamingChat } from '@/contexts/StreamingChatContext';
import { useAuth } from '@/contexts/AuthContext';
import type { ChatMessage, ArtifactInfo, ThreadDetailResponse } from '@/types/chat';

const POLL_INTERVAL = 5000;

const ChatThreadPage: React.FC = () => {
  const { threadId } = useParams<{ threadId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { toggleSidebar } = useChatLayout();
  const { user } = useAuth();

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleInput, setTitleInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showShareConfirm, setShowShareConfirm] = useState(false);

  const [artifactPanel, setArtifactPanel] = useState<ArtifactInfo | null>(null);

  const {
    isStreaming,
    isAborting,
    lastStopReason,
    liveSegments,
    thinkingText,
    latestArtifact,
    error: streamError,
    sendMessage: sendV2Message,
    abortStream,
    reset: resetStream,
  } = useStreamingChat();

  const {
    data: threadData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['chat-thread', threadId],
    queryFn: () => chatApi.getThread(threadId!),
    enabled: !!threadId,
    refetchInterval: isStreaming ? false : POLL_INTERVAL,
  });

  const thread = threadData?.data?.thread;
  const messages: ChatMessage[] = threadData?.data?.messages ?? [];

  useEffect(() => {
    if (thread?.title) {
      setTitleInput(thread.title);
    }
  }, [thread?.title]);

  // Detect stream completion (isStreaming: true → false) to refresh data
  const wasStreamingRef = useRef(false);
  useEffect(() => {
    if (wasStreamingRef.current && !isStreaming && !streamError) {
      queryClient.invalidateQueries({ queryKey: ['chat-thread', threadId] });
      queryClient.invalidateQueries({ queryKey: ['chat-threads'] });
      setIsSending(false);
      resetStream();
    }
    wasStreamingRef.current = isStreaming;
  }, [isStreaming, streamError, queryClient, threadId, resetStream]);

  useEffect(() => {
    if (streamError) {
      toast.error(streamError);
      setIsSending(false);
    }
  }, [streamError]);

  // Auto-open preview panel when a new artifact arrives via SSE
  useEffect(() => {
    if (latestArtifact) {
      setArtifactPanel(latestArtifact);
    }
  }, [latestArtifact]);

  const updateThreadMutation = useMutation({
    mutationFn: async (updates: { title?: string; status?: 'active' | 'resolved' | 'archived' }) => {
      return chatApi.updateThread(threadId!, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-thread', threadId] });
      queryClient.invalidateQueries({ queryKey: ['chat-threads'] });
      setIsEditingTitle(false);
      toast.success('Thread updated');
    },
    onError: (error) => {
      toast.error(handleApiError(error) || 'Failed to update thread');
    },
  });

  const resolveThreadMutation = useMutation({
    mutationFn: () => chatApi.resolveThread(threadId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-thread', threadId] });
      queryClient.invalidateQueries({ queryKey: ['chat-threads'] });
      toast.success('Thread marked as resolved');
    },
  });

  const archiveThreadMutation = useMutation({
    mutationFn: () => chatApi.archiveThread(threadId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-threads'] });
      toast.success('Thread archived');
      navigate('/chat');
    },
  });

  const deleteThreadMutation = useMutation({
    mutationFn: () => chatApi.deleteThread(threadId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-threads'] });
      toast.success('Thread deleted');
      navigate('/chat');
    },
  });

  const shareThreadMutation = useMutation({
    mutationFn: () => chatApi.shareThread(threadId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-thread', threadId] });
      queryClient.invalidateQueries({ queryKey: ['chat-threads'] });
      toast.success('Thread shared with team');
    },
    onError: (error) => {
      toast.error(handleApiError(error) || 'Failed to share thread');
    },
  });

  const handleSendMessage = useCallback(
    async (content: string, uploadIds?: string[]) => {
      if (!threadId) return;
      setIsSending(true);
      resetStream();

      queryClient.setQueryData<ThreadDetailResponse>(['chat-thread', threadId], (oldData) => {
        if (!oldData?.data) return oldData;
        const optimisticMsg: ChatMessage = {
          id: `optimistic-${Date.now()}`,
          thread_id: threadId,
          role: 'user',
          segments: [{ type: 'text', text: content }],
          thinking_content: null,
          triggered_by_llm_interaction_id: null,
          attachments: null,
          created_at: new Date().toISOString(),
        };
        return {
          ...oldData,
          data: {
            ...oldData.data,
            messages: [...oldData.data.messages, optimisticMsg],
          },
        };
      });

      try {
        await sendV2Message(threadId, content, uploadIds);
      } catch {
        setIsSending(false);
      }
    },
    [threadId, sendV2Message, resetStream, queryClient]
  );

  const handleArtifactClick = useCallback((artifact: ArtifactInfo) => {
    setArtifactPanel(artifact);
  }, []);

  const activeToolCallId = searchParams.get('tool');

  const handleToolCallClick = useCallback(
    (toolCallId: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('tool', toolCallId);
        return next;
      });
    },
    [setSearchParams]
  );

  const handleToolCallDrawerClose = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('tool');
      return next;
    });
  }, [setSearchParams]);

  const handleSaveTitle = () => {
    if (titleInput.trim() && titleInput !== thread?.title) {
      updateThreadMutation.mutate({ title: titleInput.trim() });
    } else {
      setIsEditingTitle(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !thread) {
    return (
      <div className="flex flex-col items-center justify-center min-h-96 text-center">
        <AlertCircle className="w-12 h-12 text-destructive mb-4" />
        <h2 className="text-xl font-semibold text-foreground mb-2">
          Failed to load conversation
        </h2>
        <p className="text-muted-foreground mb-4">
          {(error as Error)?.message || 'Thread not found'}
        </p>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate('/chat')}>
            Back to Chats
          </Button>
          <Button onClick={() => refetch()}>Try Again</Button>
        </div>
      </div>
    );
  }

  const isActive = thread.status === 'active';
  const isAuthor = thread.user_id === user?.id;
  const isReadOnly = !isAuthor;
  const displayTitle = thread.title || 'Untitled conversation';

  return (
    <div className="flex h-screen">
      {/* Chat area */}
      <div className={clsx('flex flex-col flex-1 min-w-0', artifactPanel && 'max-w-[60%]')}>
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 h-14 border-b border-border">
        {/* Left: hamburger + title */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-lg text-muted-foreground hover:bg-accent transition-colors"
            aria-label="Toggle sidebar"
          >
            <MenuIcon className="w-5 h-5" />
          </button>

          <div className="flex-1 min-w-0">
            {isEditingTitle ? (
              <input
                type="text"
                value={titleInput}
                onChange={(e) => setTitleInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveTitle();
                  if (e.key === 'Escape') setIsEditingTitle(false);
                }}
                onBlur={handleSaveTitle}
                autoFocus
                className={clsx(
                  'w-full px-2 py-1 text-lg font-semibold rounded border',
                  'border-primary focus:outline-none focus:ring-2 focus:ring-ring/40',
                  'bg-card text-foreground'
                )}
              />
            ) : (
              <h1 className="text-lg font-semibold text-foreground truncate">
                {displayTitle}
              </h1>
            )}
          </div>
        </div>

        {/* Right: ellipsis menu (author only) */}
        {isAuthor && <Menu as="div" className="relative flex-shrink-0">
          <Menu.Button className="p-2 rounded-lg text-muted-foreground hover:bg-accent transition-colors">
            <MoreVertical className="w-5 h-5" />
          </Menu.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right rounded-xl bg-card shadow-lg ring-1 ring-black/5 dark:ring-white/10 focus:outline-none z-10">
              <div className="p-1">
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={() => {
                        setTitleInput(thread.title || '');
                        setIsEditingTitle(true);
                      }}
                      className={clsx(
                        'flex items-center gap-2 w-full px-3 py-2 text-sm rounded-lg',
                        'text-foreground',
                        active && 'bg-accent'
                      )}
                    >
                      <Edit3 className="w-4 h-4" />
                      Rename
                    </button>
                  )}
                </Menu.Item>
                {thread.visibility === 'personal' && thread.user_id === user?.id && (
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => setShowShareConfirm(true)}
                        className={clsx(
                          'flex items-center gap-2 w-full px-3 py-2 text-sm rounded-lg',
                          'text-foreground',
                          active && 'bg-accent'
                        )}
                      >
                        <Share2 className="w-4 h-4 text-info" />
                        Share with team
                      </button>
                    )}
                  </Menu.Item>
                )}
                {thread.visibility === 'shared' && (
                  <div className="flex items-center gap-2 px-3 py-2 text-xs text-info">
                    <Share2 className="w-3.5 h-3.5" />
                    Shared with team
                  </div>
                )}
                {isActive && (
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => resolveThreadMutation.mutate()}
                        className={clsx(
                          'flex items-center gap-2 w-full px-3 py-2 text-sm rounded-lg',
                          'text-foreground',
                          active && 'bg-accent'
                        )}
                      >
                        <CheckCircle className="w-4 h-4 text-success" />
                        Mark as Resolved
                      </button>
                    )}
                  </Menu.Item>
                )}
                {thread.status !== 'archived' && (
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => archiveThreadMutation.mutate()}
                        className={clsx(
                          'flex items-center gap-2 w-full px-3 py-2 text-sm rounded-lg',
                          'text-foreground',
                          active && 'bg-accent'
                        )}
                      >
                        <Archive className="w-4 h-4" />
                        Archive
                      </button>
                    )}
                  </Menu.Item>
                )}
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className={clsx(
                        'flex items-center gap-2 w-full px-3 py-2 text-sm rounded-lg',
                        'text-destructive',
                        active && 'bg-destructive/15'
                      )}
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  )}
                </Menu.Item>
              </div>
            </Menu.Items>
          </Transition>
        </Menu>}
      </div>

      {/* Messages area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <MessageList
          messages={messages}
          isStreaming={isStreaming}
          liveSegments={liveSegments}
          thinkingText={thinkingText}
          threadId={threadId}
          lastStopReason={lastStopReason}
          onArtifactClick={handleArtifactClick}
          onToolCallClick={handleToolCallClick}
        />

        {/* Input area */}
        {isReadOnly ? (
          <div className="border-t border-border bg-muted/50 px-4 py-3 text-center">
            <p className="text-sm text-muted-foreground">
              Shared by {thread.author_name || 'a team member'} — view only
            </p>
          </div>
        ) : isActive ? (
          <MessageInput
            onSend={handleSendMessage}
            disabled={isSending}
            isStreaming={isStreaming}
            isAborting={isAborting}
            onStop={threadId ? () => abortStream(threadId) : undefined}
            placeholder={
              thread.pending_question_session_id
                ? 'Answer the AI\'s question...'
                : 'Ask about your business data...'
            }
          />
        ) : (
          <div className="border-t border-border bg-muted/50 px-4 py-3 text-center">
            <p className="text-sm text-muted-foreground">
              This conversation is {thread.status}. Start a new chat to continue.
            </p>
          </div>
        )}
      </div>
      </div>

      {/* Artifact preview panel */}
      {artifactPanel && (
        <div className="w-[40%] flex-shrink-0 hidden md:block">
          <ArtifactPreviewPanel
            artifact={artifactPanel}
            isOpen={!!artifactPanel}
            onClose={() => setArtifactPanel(null)}
          />
        </div>
      )}

      {/* Delete confirmation modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => deleteThreadMutation.mutate()}
        title="Delete conversation"
        message="Are you sure you want to delete this conversation? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
        isLoading={deleteThreadMutation.isPending}
      />

      {/* Share confirmation modal */}
      <ConfirmationModal
        isOpen={showShareConfirm}
        onClose={() => setShowShareConfirm(false)}
        onConfirm={() => shareThreadMutation.mutate()}
        title="Share with team"
        message="This will make this conversation visible to all team members. You cannot undo this."
        confirmText="Share"
        cancelText="Cancel"
        isLoading={shareThreadMutation.isPending}
      />

      {/* Tool call detail drawer (driven by ?tool= search param) */}
      <ToolCallDrawer
        toolCallId={activeToolCallId}
        onClose={handleToolCallDrawerClose}
      />
    </div>
  );
};

export default ChatThreadPage;
